import {
    type ButtonHTMLAttributes,
    type ReactNode,
    forwardRef,
    useState,
} from 'react';

import { type LucideIcon } from 'lucide-react';
import { Check, ChevronsUpDown, Loader2, Plus, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

interface TypeComboboxItem {
    value: string;
    label: string;
}

interface TypeComboboxProps {
    items: TypeComboboxItem[];
    isLoading?: boolean;
    /** Disables the trigger without showing the `isLoading` spinner — e.g. a
     *  cascading field that has no options until an earlier field is set. */
    disabled?: boolean;
    selectedValue: string;
    onSelect: (value: string) => void;
    onCreate?: (name: string) => void;
    isCreating?: boolean;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    heading?: string;
    icon?: LucideIcon;
    /** Icon shown at the trailing edge of the trigger (replaced by a spinner
     *  while `isCreating`). Defaults to `ChevronsUpDown`; pass a different
     *  icon (e.g. `ChevronDown`, to match a sibling field) or `null` to hide
     *  it entirely. */
    chevronIcon?: LucideIcon | null;
    allowClear?: boolean;
    testId?: string;
    /** Extra classes for the trigger button, e.g. to match sibling inputs. */
    triggerClassName?: string;
    /**
     * When true, the trigger falls back to showing the raw `selectedValue`
     * (instead of the placeholder) if it matches no item — for free-typed
     * custom values (e.g. the Ground Truth split editors). Defaults to false
     * so callers whose `selectedValue` can transiently be a not-yet-loaded id
     * (e.g. a newly created collection) keep showing the placeholder.
     */
    showValueWhenUnmatched?: boolean;
    /** Replaces cmdk's default fuzzy matching: returns above 0 when an item's label matches the search. */
    filter?: (label: string, search: string) => number;
    /** Replaces the empty message when the search matches nothing; `select` picks an item and closes the list, `close` just closes it. */
    renderEmpty?: (context: {
        query: string;
        select: (value: string) => void;
        close: () => void;
    }) => ReactNode;
}

/** Hub type picker: SearchableCombobox plus custom matching (`filter`) and empty state (`renderEmpty`).
 *  Forwards to the trigger button so a form's `field.ref` has a focusable node. */
export const TypeCombobox = forwardRef<
    HTMLButtonElement,
    TypeComboboxProps &
        Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect' | 'disabled'>
>(function TypeCombobox(
    {
        items,
        isLoading = false,
        disabled = false,
        selectedValue,
        onSelect,
        onCreate,
        isCreating = false,
        placeholder = 'Select an option',
        searchPlaceholder = 'Search...',
        emptyMessage = 'No results found.',
        heading,
        icon: Icon,
        chevronIcon: ChevronIcon = ChevronsUpDown,
        allowClear = true,
        testId,
        showValueWhenUnmatched = false,
        renderEmpty,
        filter,
        triggerClassName,
        ...buttonProps
    },
    ref,
) {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const selectedItem = items.find((item) => item.value === selectedValue);
    const isDisabled = isLoading || disabled;
    // The button's own disabled:opacity-50 dims the whole trigger uniformly,
    // which would wash out an error border into a weak, inconsistent pink —
    // so the border opts out via disabled:opacity-100 below. That opts the
    // icon/text out of the dimming too, so re-apply it to just those to keep
    // the "you can't click this" cue while the border stays fully visible.
    // (The chevron already dims independently of disabled state.)
    const dimDisabledContent = isDisabled && buttonProps['aria-invalid'];

    const showCreateOption =
        onCreate &&
        searchValue.trim() !== '' &&
        !items.some(
            (item) =>
                item.label.toLowerCase() === searchValue.trim().toLowerCase(),
        );

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setSearchValue('');
                }
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    ref={ref}
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    type="button"
                    {...buttonProps}
                    className={cn(
                        'w-full justify-between',
                        triggerClassName,
                        buttonProps['aria-invalid'] && 'border-destructive',
                        // Opt the border out of the button's own
                        // disabled:opacity-50 — it would otherwise wash
                        // border-destructive out into a weak, inconsistent
                        // pink. The icon/text below re-apply the dimming so
                        // the "you can't click this" cue isn't lost.
                        buttonProps['aria-invalid'] && 'disabled:opacity-100',
                    )}
                    data-testid={testId}
                    disabled={isDisabled}
                >
                    <div className="flex min-w-0 items-center gap-2">
                        {Icon && (
                            <Icon
                                className={cn(
                                    'h-4 w-4 shrink-0 text-muted-foreground',
                                    dimDisabledContent && 'opacity-50',
                                )}
                            />
                        )}
                        <span
                            className={cn(
                                'truncate font-normal leading-6',
                                !selectedItem &&
                                    !(showValueWhenUnmatched && selectedValue)
                                    ? 'text-slate-600'
                                    : 'text-slate-900',
                                dimDisabledContent && 'opacity-50',
                            )}
                        >
                            {selectedItem?.label ??
                                (showValueWhenUnmatched && selectedValue
                                    ? selectedValue
                                    : placeholder)}
                        </span>
                    </div>
                    {isCreating ? (
                        <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                        ChevronIcon && (
                            <ChevronIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        )
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-(--radix-popover-trigger-width) p-0"
                align="start"
            >
                <Command shouldFilter filter={filter}>
                    <CommandInput
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onValueChange={setSearchValue}
                        className="font-normal"
                    />
                    <CommandList>
                        <CommandEmpty>
                            {renderEmpty && searchValue.trim() !== ''
                                ? renderEmpty({
                                      query: searchValue.trim(),
                                      select: (value) => {
                                          onSelect(value);
                                          setOpen(false);
                                      },
                                      close: () => setOpen(false),
                                  })
                                : emptyMessage}
                        </CommandEmpty>
                        <CommandGroup heading={heading}>
                            {items.map((item) => {
                                const isSelected = item.value === selectedValue;
                                return (
                                    <CommandItem
                                        key={item.value}
                                        value={item.label}
                                        onSelect={() => {
                                            onSelect(item.value);
                                            setOpen(false);
                                        }}
                                        className="gap-2 font-normal"
                                    >
                                        <Check
                                            className={cn(
                                                'h-4 w-4',
                                                isSelected
                                                    ? 'opacity-100'
                                                    : 'opacity-0',
                                            )}
                                        />
                                        {item.label}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>

                        {showCreateOption && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        value={`create-${searchValue.trim()}`}
                                        onSelect={() => {
                                            onCreate(searchValue.trim());
                                            setOpen(false);
                                            setSearchValue('');
                                        }}
                                        className="gap-2 font-normal"
                                    >
                                        <Plus className="h-4 w-4" />
                                        Create &quot;{searchValue.trim()}&quot;
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}

                        {allowClear && selectedValue && (
                            <>
                                <CommandSeparator />
                                <CommandGroup>
                                    <CommandItem
                                        value="clear-selection"
                                        onSelect={() => {
                                            onSelect('');
                                            setOpen(false);
                                        }}
                                        className="gap-2 font-normal text-muted-foreground"
                                    >
                                        <X className="h-4 w-4" />
                                        Clear selection
                                    </CommandItem>
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
});
